/**
 * useAudioRecorder — Records audio via browser mic, saves to Supabase Storage
 * FIRST, then transcribes (Whisper) and structures (Claude).
 *
 * KEY DESIGN: Audio is persisted BEFORE any processing. If transcription or
 * structuring fails, the audio is still safely stored and can be retried later.
 *
 * BACKGROUND PROTECTION: Uses a silent AudioContext oscillator + Wake Lock API
 * to prevent Chrome from suspending the tab during recording. Also monitors
 * visibilitychange to detect if the MediaRecorder stopped unexpectedly.
 *
 * LIVE SINGLETON: o estado da gravação ATIVA mora fora do React (liveRecording).
 * Trocar de aba interna da ficha desmonta o componente sem matar a gravação;
 * ao voltar, o hook ADOTA a gravação (timer/chunks/Parar religados). Sair da
 * página (link SPA) com confirmação = abandono gracioso → recovery do backend.
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { supabase } from '../../lib/supabaseClient';
import { webhookBase } from '../../lib/webhookBase';
import {
  vaultSaveMeta,
  vaultPutChunk,
  vaultDeleteChunk,
  vaultDropSession,
} from '../../lib/recordingVault';
import i18n from '../../i18n';
import { webhookFetch } from '../../lib/webhookFetch';
import { fetchTranscricao, SemSessao } from '../../lib/transcricaoComLogin';
import {
  procurarOutraGravacao,
  lerRecusaDoBanco,
  registrarFonteDaGravacao,
  segurarTravaDaGravacao,
  soltarTravaDaGravacao,
  OutraGravacao,
  FimDaParada,
} from '../../lib/gravacao/umaPorComputador';

export type RecorderStatus =
  | 'idle'
  | 'recording'
  | 'uploading'
  | 'transcribing'
  | 'structuring'
  | 'saving'
  | 'done'
  | 'error';

export interface UseAudioRecorderReturn {
  /** o pedido do microfone está em andamento (o botão de gravar fica travado) */
  iniciando: boolean;
  status: RecorderStatus;
  elapsedSeconds: number;
  error: string | null;
  transcript: string | null;
  /** True when upload failed but blob is preserved — can retry */
  hasSavedBlob: boolean;
  /** True when recording may have been affected by tab suspension */
  backgroundWarning: boolean;
  /** Nº de pedaços já salvos no backup incremental (UX "salvando ao vivo") */
  chunksSaved: number;
  /** True quando o servidor não confirma um pedaço há >45s DURANTE a gravação.
   *  É o aviso que faltou no caso Sandra (31/08): o timer seguia rodando com o
   *  envio morto, e ninguém tinha como saber antes de fechar a aba. */
  uploadStalled: boolean;
  /** Se o backup incremental está ligado nesta build */
  backupEnabled: boolean;
  /** True quando a gravação ficou salva e a transcrição seguiu pelo servidor
   *  (a sessão do navegador tinha expirado). Não é falha: fica pronta sozinha. */
  transcrevendoNoServidor: boolean;
  /** outra gravação do mesmo computador ou login (02/10): a tela pergunta antes de começar esta */
  outraGravacao: OutraGravacao | null;
  limparOutraGravacao: () => void;
  startRecording: () => Promise<void>;
  stopRecording: () => Promise<void>;
  reset: () => void;
  /** Retry full upload+transcribe using preserved blob */
  retryUpload: () => Promise<void>;
  /** Retry transcription for a previously saved recording */
  retryTranscription: (recordingId: string, audioPath: string) => Promise<void>;
}

export function getWebhookUrl(): string {
  return webhookBase();
}

// ── Backup incremental (rede de segurança anti-perda) ─────────────────────────
// Enquanto grava, sobe um pedaço a cada CHUNK_MS pro Storage. Se a aba fechar /
// cair a internet no meio, o backend recupera juntando os pedaços (migration 141
// + webhook/recordingRecovery). Build-time flag (CRA): default LIGADO; setar
// REACT_APP_STREAMING_BACKUP_ENABLED='false' desliga e volta ao single-blob de antes.
// O fluxo do "Parar" NÃO muda — o blob da RAM continua sendo a fonte no caminho feliz.
const STREAMING_BACKUP_ENABLED =
  process.env.REACT_APP_STREAMING_BACKUP_ENABLED !== 'false';
const CHUNK_MS = STREAMING_BACKUP_ENABLED ? 15_000 : 1_000;

// ── Gravação ao vivo: singleton FORA do React ─────────────────────────────────
// Trocar de aba interna na ficha desmonta o componente, mas o MediaRecorder não
// pode morrer (a Dra. espia Labs no meio da consulta). O estado vivo mora aqui;
// ao remontar, o hook ADOTA a gravação em andamento (timer, chunks, botão Parar).
interface LiveRecording {
  patientId: string;
  patientName: string;
  source: 'portal' | 'nurse_note';
  recorder: MediaRecorder;
  stream: MediaStream;
  chunks: Blob[];
  sessionId: string | null;
  chunkIndex: number;
  storagePrefix: string | null;
  pendingChunks: { index: number; blob: Blob }[];
  startTimestamp: number;
  startTimeIso: string;
  chunksSaved: number;
  /** Date.now() da ultima CONFIRMACAO do servidor — alimenta o aviso de atraso. */
  lastConfirmedAt: number;
  audioCtx: AudioContext | null;
  wakeLock: any;
  listeners: Set<() => void>;
  /** Rota da ficha de origem — o selo flutuante navega de volta pra cá */
  returnTo: string;
  /** outra tela pediu para esta gravação finalizar (migration 436) */
  paradaPedida?: boolean;
}

let liveRecording: LiveRecording | null = null;
// um início por vez em toda a aba (01/10): com a permissão do microfone pendente ("prompt"), o segundo clique em
// "Gravar" criava uma SEGUNDA gravação quando a permissão saía — uma ficava zumbi, gravando sem a tela saber
let iniciandoGravacao = false;

// Nº de RecordingControls montados EXIBINDO a gravação — quando 0 e há gravação
// viva, o selo flutuante global (GlobalRecordingPill) aparece.
let recordingUiVisible = 0;

/** Info pública da gravação ao vivo (pro selo flutuante — sem expor o recorder). */
export interface LiveRecordingPublicInfo {
  patientId: string;
  patientName: string;
  source: 'portal' | 'nurse_note';
  startTimestamp: number;
  chunksSaved: number;
  returnTo: string;
}

export function getLiveRecordingInfo(): LiveRecordingPublicInfo | null {
  const lr = liveRecording;
  if (!lr || lr.recorder.state === 'inactive') return null;
  const { patientId, patientName, source, startTimestamp, chunksSaved, returnTo } = lr;
  return { patientId, patientName, source, startTimestamp, chunksSaved, returnTo };
}

export function isRecordingUiMounted(): boolean {
  return recordingUiVisible > 0;
}

/** Stream do mic da gravação ao vivo — pro waveform animado (AnalyserNode). */
export function getLiveRecordingStream(): MediaStream | null {
  const lr = liveRecording;
  return lr && lr.recorder.state !== 'inactive' ? lr.stream : null;
}

// ── UMA GRAVAÇÃO POR VEZ (02/10) ───────────────────────────────────────────────
// quem pediu "Finalizar a outra" espera o fim do fluxo normal do Parar: 'salva' = áudio no Storage + plaud_recordings
// + sessão 'finalized' (a transcrição segue depois, como sempre)
type OuvinteDoFim = (sessao: string | null, fim: FimDaParada) => void;
const ouvintesDoFim = new Set<OuvinteDoFim>();
export function aoFimDaGravacao(fn: OuvinteDoFim): () => void {
  ouvintesDoFim.add(fn);
  return () => { ouvintesDoFim.delete(fn); };
}
function avisarFimDaGravacao(sessao: string | null, fim: FimDaParada) {
  ouvintesDoFim.forEach((fn) => { try { fn(sessao, fim); } catch { /* ouvinte não derruba o Parar */ } });
}
// pedido de parada vindo do BANCO (outro aparelho marcou a sessão): o gravador global para pelo fluxo normal
let aoPedidoDeParada: (() => void) | null = null;
export function registrarPedidoDeParada(fn: (() => void) | null) { aoPedidoDeParada = fn; }
// a outra aba pergunta "quem grava?": esta responde com o paciente e desde quando
registrarFonteDaGravacao(() => {
  const lr = liveRecording;
  if (!lr || lr.recorder.state === 'inactive') return null;
  return { paciente: lr.patientName, pacienteId: lr.patientId, desde: lr.startTimeIso, sessao: lr.sessionId, returnTo: lr.returnTo };
});

function liveNotify(lr: LiveRecording) {
  lr.listeners.forEach((fn) => fn());
}

/** Sobe 1 chunk de backup pro Storage. Best-effort: falha vai pra fila de retry. */
async function liveUploadChunk(lr: LiveRecording, blob: Blob, index: number) {
  if (!lr.storagePrefix || !lr.sessionId) return;
  const path = `${lr.storagePrefix}/chunk-${String(index).padStart(4, '0')}.webm`;
  try {
    const { error: upErr } = await supabase.storage
      .from('consultation-audio')
      .upload(path, blob, { contentType: 'audio/webm', upsert: true });
    if (upErr) throw upErr;
    await supabase
      .from('recording_sessions')
      .update({
        last_chunk_at: new Date().toISOString(),
        chunk_count: index + 1,
        updated_at: new Date().toISOString(),
      })
      .eq('id', lr.sessionId);
    lr.chunksSaved = Math.max(lr.chunksSaved, index + 1);
    // O SERVIDOR CONFIRMOU → o pedaco sai do cofre local. A ordem importa:
    // apagar aqui (e nunca na tentativa) e o que faz o cofre conter sempre
    // exatamente o que falta subir — desenho do Valdair, caso Sandra 31/08.
    lr.lastConfirmedAt = Date.now();
    vaultDeleteChunk(lr.sessionId!, index); // fire-and-forget, best-effort
    liveNotify(lr); // UX ao vivo: "X pedaços salvos"
    // outra tela (outro aparelho) pediu para esta finalizar? (436) — leitura à parte: erro aqui não vira pedaço perdido
    if (!lr.paradaPedida) {
      supabase.from('recording_sessions').select('parar_pedido_em').eq('id', lr.sessionId).maybeSingle()
        .then(({ data }) => {
          if (data && (data as any).parar_pedido_em && !lr.paradaPedida && liveRecording === lr) {
            lr.paradaPedida = true;
            aoPedidoDeParada?.();
          }
        }, () => undefined);
    }
  } catch (err: any) {
    // Rede caiu? Guarda pra re-tentar no próximo chunk.
    lr.pendingChunks.push({ index, blob });
    console.warn('[useAudioRecorder] chunk backup falhou (na fila de retry):', err?.message);
  }
}

/** Re-tenta subir chunks que falharam antes (chamado a cada novo chunk). */
async function liveFlushPending(lr: LiveRecording) {
  if (lr.pendingChunks.length === 0) return;
  const pending = lr.pendingChunks;
  lr.pendingChunks = [];
  for (const { index, blob } of pending) {
    await liveUploadChunk(lr, blob, index);
  }
}

// Guarda global da FASE DE GRAVAÇÃO — vive fora do componente porque a gravação
// continua mesmo com ele desmontado. Navegação DENTRO do portal é LIVRE (o selo
// flutuante GlobalRecordingPill mantém o mic sempre visível); só fechar a
// aba/navegador pede confirmação — e se fechar mesmo assim, o recovery salva.
function liveBeforeUnloadGuard(e: BeforeUnloadEvent) {
  e.preventDefault();
  e.returnValue = '';
}

function installLiveGuards() {
  window.addEventListener('beforeunload', liveBeforeUnloadGuard);
  segurarTravaDaGravacao(); // as outras abas do navegador sabem que esta grava
}

function removeLiveGuards() {
  window.removeEventListener('beforeunload', liveBeforeUnloadGuard);
  soltarTravaDaGravacao();
}

export function useAudioRecorder(
  patientId: string,
  patientName: string,
  // 'nurse_note' = nota de voz da enfermagem: transcreve igual, mas NÃO dispara
  // SOAP médico automático (triggers filtram por source — migration 119)
  source: 'portal' | 'nurse_note' = 'portal',
  /** invisivel: o gravador global (faixa do topo) — não conta como UI que exibe a gravação */
  opcoes: { invisivel?: boolean } = {},
): UseAudioRecorderReturn {
  const invisivel = !!opcoes.invisivel;
  const fileLabel = source === 'nurse_note' ? 'Nota de Enfermagem' : 'Portal Recording';
  const [status, setStatus] = useState<RecorderStatus>('idle');
  /** o pedido do microfone está em andamento (o botão fica travado) */
  const [iniciando, setIniciando] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [transcript, setTranscript] = useState<string | null>(null);
  const [backgroundWarning, setBackgroundWarning] = useState(false);
  const [chunksSaved, setChunksSaved] = useState(0);
  const [uploadStalled, setUploadStalled] = useState(false);
  const [transcrevendoNoServidor, setTranscrevendoNoServidor] = useState(false);
  const [outraGravacao, setOutraGravacao] = useState<OutraGravacao | null>(null);
  // ESTA instância está parando a gravação (as outras que a exibiam voltam a 'idle' sozinhas)
  const parandoRef = useRef(false);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const lastBlobRef = useRef<Blob | null>(null); // Preserve blob if upload fails
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startTimestampRef = useRef<number>(0); // Date.now() when recording started
  const startTimeRef = useRef<string | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  // Background protection refs
  const audioCtxRef = useRef<AudioContext | null>(null);
  const wakeLockRef = useRef<any>(null);
  // Chunked backup refs (rede de segurança — não afetam o fluxo feliz)
  const sessionIdRef = useRef<string | null>(null);
  const storagePrefixRef = useRef<string | null>(null);

  // Adoção: religa a UI numa gravação ao vivo já existente deste paciente
  const adoptLive = useCallback((lr: LiveRecording) => {
    mediaRecorderRef.current = lr.recorder;
    streamRef.current = lr.stream;
    chunksRef.current = lr.chunks;
    sessionIdRef.current = lr.sessionId;
    storagePrefixRef.current = lr.storagePrefix;
    startTimestampRef.current = lr.startTimestamp;
    startTimeRef.current = lr.startTimeIso;
    audioCtxRef.current = lr.audioCtx;
    wakeLockRef.current = lr.wakeLock;
    parandoRef.current = false;
    setChunksSaved(lr.chunksSaved);
    setElapsedSeconds(Math.round((Date.now() - lr.startTimestamp) / 1000));
    setStatus('recording');
  }, []);

  // Vigia do singleton: cobre TODAS as corridas (componente desmontado por troca
  // de aba interna e remontado; remount por refetch ANTES da gravação começar;
  // getUserMedia resolvendo depois de um remount). Se a UI está parada mas existe
  // gravação viva deste paciente nesta página, adota em até 1s.
  useEffect(() => {
    if (status !== 'idle') return;
    const tryAdopt = () => {
      const lr = liveRecording;
      if (!lr || lr.patientId !== patientId || lr.source !== source) return;
      if (lr.recorder.state === 'inactive') {
        // Gravação já terminou numa closure antiga — pipeline/recovery cuidam.
        if (liveRecording === lr) liveRecording = null;
        return;
      }
      adoptLive(lr);
    };
    tryAdopt(); // imediato no mount
    const iv = setInterval(tryAdopt, 1000);
    return () => clearInterval(iv);
  }, [status, patientId, source, adoptLive]);

  // Assina o store enquanto grava: mantém "X pedaços salvos" vivo na UI
  useEffect(() => {
    if (status !== 'recording') return;
    const lr = liveRecording;
    if (!lr) return;
    const listener = () => setChunksSaved(lr.chunksSaved);
    lr.listeners.add(listener);
    return () => { lr.listeners.delete(listener); };
  }, [status]);

  // Enquanto ESTA UI exibe a gravação, o selo flutuante global fica escondido
  useEffect(() => {
    if (status !== 'recording' || invisivel) return;
    recordingUiVisible++;
    return () => { recordingUiVisible--; };
  }, [status, invisivel]);

  // Timer: use real timestamp difference (survives tab throttling)
  useEffect(() => {
    if (status === 'recording') {
      timerRef.current = setInterval(() => {
        if (startTimestampRef.current) {
          setElapsedSeconds(Math.round((Date.now() - startTimestampRef.current) / 1000));
        }
        // Aviso de envio parado: >45s sem CONFIRMAÇÃO do servidor. 45s = 3
        // pedaços perdidos — acima do soluço normal de rede, cedo o bastante
        // pra alguém agir com a consulta ainda acontecendo.
        const lr = liveRecording;
        setUploadStalled(Boolean(
          lr && lr.sessionId && Date.now() - lr.lastConfirmedAt > 45_000,
        ));
        // outra instância (a faixa do topo, ou o pedido de outra aba) parou esta gravação: esta tela volta ao início
        if (!parandoRef.current && (!lr || lr.recorder !== mediaRecorderRef.current)) {
          setStatus('idle');
        }
      }, 1000);
    } else if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
      setUploadStalled(false);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [status]);

  // Background protection: detect tab visibility changes during recording
  useEffect(() => {
    const handleVisibility = () => {
      if (document.hidden && status === 'recording') {
        // Tab went to background — check recorder state shortly after
        setTimeout(() => {
          const recorder = mediaRecorderRef.current;
          if (recorder && recorder.state === 'inactive' && status === 'recording') {
            setBackgroundWarning(true);
            console.warn('[useAudioRecorder] MediaRecorder stopped in background');
          }
        }, 2000);
      }
      if (!document.hidden && status === 'recording') {
        // Tab came back — verify recorder is still running
        const recorder = mediaRecorderRef.current;
        if (recorder && recorder.state === 'inactive') {
          setBackgroundWarning(true);
        }
        // Re-acquire wake lock (released when tab goes hidden on some browsers)
        if (!wakeLockRef.current && (navigator as any).wakeLock) {
          (navigator as any).wakeLock.request('screen').then((lock: any) => {
            wakeLockRef.current = lock;
          }).catch(() => {});
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [status]);

  // Warn before unload while PROCESSING (a fase de gravação é coberta pelas
  // guardas de módulo install/removeLiveGuards, que sobrevivem ao unmount)
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (
        status === 'uploading' ||
        status === 'transcribing' ||
        status === 'structuring' ||
        status === 'saving'
      ) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [status]);

  // Block navigation while PROCESSING — intercept link clicks (gravação: guarda de módulo)
  useEffect(() => {
    const isActive = status === 'uploading' || status === 'transcribing' || status === 'structuring' || status === 'saving';
    if (!isActive) return;

    const handleClick = (e: MouseEvent) => {
      const target = (e.target as HTMLElement).closest('a[href], [role="link"], [data-testid="sidebar-link"]');
      if (!target) return;
      const href = target.getAttribute('href');
      if (!href || href === '#' || href === window.location.pathname) return;
      const msg = i18n.language === 'pt'
        ? 'Gravação em andamento! Se sair desta página, a gravação será perdida. Deseja sair mesmo assim?'
        : 'Recording in progress! If you leave this page, the recording will be lost. Leave anyway?';
      if (!window.confirm(msg)) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    document.addEventListener('click', handleClick, true);
    return () => document.removeEventListener('click', handleClick, true);
  }, [status]);

  /** Start silent AudioContext + Wake Lock to keep tab alive */
  const startKeepAlive = useCallback(async () => {
    // Silent oscillator prevents Chrome from suspending the tab
    try {
      const ctx = new AudioContext();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      gain.gain.value = 0; // completely silent
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start();
      audioCtxRef.current = ctx;
    } catch (e) {
      console.warn('[useAudioRecorder] AudioContext keep-alive failed:', e);
    }
    // Wake Lock prevents screen from turning off
    try {
      if ((navigator as any).wakeLock) {
        wakeLockRef.current = await (navigator as any).wakeLock.request('screen');
      }
    } catch (e) {
      console.warn('[useAudioRecorder] Wake Lock failed:', e);
    }
  }, []);

  /** Stop keep-alive mechanisms */
  const stopKeepAlive = useCallback(() => {
    if (audioCtxRef.current) {
      audioCtxRef.current.close().catch(() => {});
      audioCtxRef.current = null;
    }
    if (wakeLockRef.current) {
      wakeLockRef.current.release().catch(() => {});
      wakeLockRef.current = null;
    }
  }, []);

  // ── Backup incremental helpers (rede de segurança) ──────────────────────────
  // (upload de chunk + fila de retry moraram aqui; agora são liveUploadChunk /
  //  liveFlushPending no módulo, porque precisam sobreviver ao unmount)

  /** Marca o status da sessão de gravação (fire-and-forget, não bloqueia o fluxo). A promessa só serve para quem
   *  espera a sessão sair de 'recording' (o "Finalizar a outra" de outra aba, 02/10). */
  const markSession = useCallback((status: 'finalized' | 'failed'): Promise<void> => {
    const sessionId = sessionIdRef.current;
    if (!sessionId) return Promise.resolve();
    return new Promise<void>((resolve) => {
      supabase
        .from('recording_sessions')
        .update({ status, updated_at: new Date().toISOString() })
        .eq('id', sessionId)
        .then(({ error }) => {
          if (error) console.warn('[useAudioRecorder] markSession falhou:', error.message);
          resolve();
        }, () => resolve());
    });
  }, []);

  /** Limpa os chunks de backup do Storage (best-effort, após finalizar normal). */
  const cleanupBackupChunks = useCallback(async () => {
    const prefix = storagePrefixRef.current;
    if (!prefix) return;
    try {
      const { data: files } = await supabase.storage.from('consultation-audio').list(prefix);
      if (files && files.length) {
        await supabase.storage
          .from('consultation-audio')
          .remove(files.map((f) => `${prefix}/${f.name}`));
      }
    } catch {
      /* best effort — o GC do backend limpa depois */
    }
  }, []);

  const startRecording = useCallback(async () => {
    if (iniciandoGravacao) return;   // o 1º clique ainda está pedindo o microfone: o 2º não cria sessão
    iniciandoGravacao = true;
    setIniciando(true);
    let sessaoCriada: string | null = null;
    try {
      // Singleton: já existe gravação ao vivo em algum lugar do app?
      const existing = liveRecording;
      if (existing && existing.recorder.state !== 'inactive') {
        if (existing.patientId === patientId) {
          adoptLive(existing); // clique no mic com gravação viva = religa a UI nela
          return;
        }
        setError(i18n.language === 'pt'
          ? `Já existe uma gravação em andamento de ${existing.patientName}. Pare-a antes de iniciar outra.`
          : `A recording for ${existing.patientName} is already in progress. Stop it before starting another.`);
        setStatus('error');
        return;
      }

      setError(null);
      setOutraGravacao(null);

      // UMA GRAVAÇÃO POR VEZ (02/10): outra aba deste navegador, ou o mesmo login em outro aparelho, gravando?
      // Então NÃO abre o microfone: a tela pergunta (finalizar a outra / ir para ela / cancelar).
      const outra = await procurarOutraGravacao();
      if (outra) { setOutraGravacao(outra); return; }

      setTranscript(null);
      setBackgroundWarning(false);
      setTranscrevendoNoServidor(false);
      setChunksSaved(0);
      setElapsedSeconds(0);

      // A sessão de recuperação nasce ANTES do microfone: o banco recusa se o mesmo login já grava (436)
      const startTimeIso = new Date().toISOString();
      let sessionId: string | null = null;
      let storagePrefix: string | null = null;
      if (STREAMING_BACKUP_ENABLED) {
        sessionId = crypto.randomUUID();
        storagePrefix = `_sessions/${patientId}/${sessionId}`;
        const { error: sessErr } = await supabase.from('recording_sessions').insert({
          id: sessionId,
          patient_id: patientId,
          patient_name: patientName,
          source,
          start_time: startTimeIso,
          status: 'recording',
          last_chunk_at: startTimeIso,
          storage_prefix: storagePrefix,
        });
        const recusa = lerRecusaDoBanco(sessErr as any);
        if (recusa) { setOutraGravacao(recusa); return; }
        if (sessErr) console.warn('[useAudioRecorder] recording_session insert falhou (backup off nesta sessão):', sessErr.message);
        else sessaoCriada = sessionId;
      }

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      // Pick supported MIME type
      let mimeType = 'audio/webm;codecs=opus';
      if (!MediaRecorder.isTypeSupported(mimeType)) {
        mimeType = 'audio/mp4';
        if (!MediaRecorder.isTypeSupported(mimeType)) {
          mimeType = ''; // let browser choose default
        }
      }

      const recorder = new MediaRecorder(stream, {
        mimeType: mimeType || undefined,
        audioBitsPerSecond: 32000,
      });

      // Estado vivo mora no MÓDULO (sobrevive a troca de aba interna da ficha)
      const lr: LiveRecording = {
        patientId,
        patientName,
        source,
        recorder,
        stream,
        chunks: [],
        sessionId: null,
        chunkIndex: 0,
        storagePrefix: null,
        pendingChunks: [],
        startTimestamp: Date.now(),
        startTimeIso,
        chunksSaved: 0,
        lastConfirmedAt: Date.now(),
        audioCtx: null,
        wakeLock: null,
        listeners: new Set(),
        // selo flutuante navega de volta pra cá ("a ficha é o foco")
        returnTo: source === 'nurse_note'
          ? window.location.pathname
          : `${window.location.pathname}?tab=consultations`,
      };

      // Rede de segurança: cria a sessão de recuperação e liga o backup incremental
      if (STREAMING_BACKUP_ENABLED && sessionId) {
        lr.sessionId = sessionId;
        lr.storagePrefix = storagePrefix;
        // Meta no cofre local: se a aba morrer, a varredura de órfãs sabe de
        // quem é o áudio e para onde ele sobe. Best-effort, nunca bloqueia.
        vaultSaveMeta({
          sessionId,
          patientId,
          patientName,
          source,
          storagePrefix: lr.storagePrefix!,
          startTimeIso: lr.startTimeIso,
          lastWriteIso: new Date().toISOString(),
        });
      }

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          lr.chunks.push(e.data); // fluxo feliz: mantém tudo na RAM (fonte do "Parar")
          if (lr.sessionId) {
            const index = lr.chunkIndex++;
            // COFRE PRIMEIRO, rede depois: o pedaço entra no disco local antes
            // da tentativa de subir, e só sai quando o servidor CONFIRMAR
            // (em liveUploadChunk). Se a rede morrer aqui, o F5 não perde nada.
            // async, fire-and-forget — NUNCA bloqueia a captura de áudio
            vaultPutChunk(lr.sessionId, index, e.data);
            liveUploadChunk(lr, e.data, index);
            liveFlushPending(lr);
          }
        }
      };

      liveRecording = lr;
      installLiveGuards();

      // Espelha no hook (o pipeline do "Parar" usa esses refs)
      mediaRecorderRef.current = recorder;
      streamRef.current = stream;
      chunksRef.current = lr.chunks;
      sessionIdRef.current = lr.sessionId;
      storagePrefixRef.current = lr.storagePrefix;
      startTimeRef.current = lr.startTimeIso;
      startTimestampRef.current = lr.startTimestamp;
      parandoRef.current = false;
      sessaoCriada = null; // daqui em diante a sessão é da gravação (o Parar e o recovery cuidam dela)
      recorder.start(CHUNK_MS); // fluxo atual = 1s; com backup = 15s (pedaço fechado por vez)

      // Keep tab alive in background
      await startKeepAlive();
      lr.audioCtx = audioCtxRef.current;
      lr.wakeLock = wakeLockRef.current;

      setStatus('recording');
    } catch (err: any) {
      console.error('[useAudioRecorder] Mic error:', err);
      // a sessão nasceu antes do microfone: sem gravação, ela sai do caminho na hora (não trava o login por 2 min)
      if (sessaoCriada) {
        supabase.from('recording_sessions').update({ status: 'failed', updated_at: new Date().toISOString() })
          .eq('id', sessaoCriada).then(undefined, () => undefined);
      }
      setError(err?.message || (i18n.language === 'pt' ? 'Erro ao acessar o microfone' : 'Could not access the microphone'));
      setStatus('error');
    } finally {
      iniciandoGravacao = false;
      setIniciando(false);
    }
  }, [startKeepAlive, patientId, patientName, source, adoptLive]);

  const stopRecording = useCallback(async () => {
    // Se houver gravação viva no singleton deste paciente, ela manda (o
    // componente pode ter sido remontado — refs podem estar desatualizados)
    const lr = liveRecording && liveRecording.patientId === patientId ? liveRecording : null;
    if (lr) {
      mediaRecorderRef.current = lr.recorder;
      streamRef.current = lr.stream;
      chunksRef.current = lr.chunks;
      sessionIdRef.current = lr.sessionId;
      storagePrefixRef.current = lr.storagePrefix;
      startTimeRef.current = lr.startTimeIso;
      audioCtxRef.current = lr.audioCtx;
      wakeLockRef.current = lr.wakeLock;
    }
    const recorder = mediaRecorderRef.current;
    if (!recorder || recorder.state === 'inactive') return;
    parandoRef.current = true;
    const sessaoDoFim = sessionIdRef.current;

    // O singleton encerra AQUI — daqui pra frente é pipeline de processamento
    if (lr) liveRecording = null;
    removeLiveGuards();

    // Stop recording and collect final chunk
    const blob = await new Promise<Blob>((resolve) => {
      recorder.onstop = () => {
        const mimeType = recorder.mimeType || 'audio/webm';
        const fullBlob = new Blob(chunksRef.current, { type: mimeType });
        resolve(fullBlob);
      };
      recorder.stop();
    });

    // Preserve blob for retry if upload fails
    lastBlobRef.current = blob;

    // Stop all mic tracks and keep-alive
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    stopKeepAlive();

    const webhookUrl = getWebhookUrl();
    const finalElapsed = startTimeRef.current
      ? Math.round((Date.now() - new Date(startTimeRef.current).getTime()) / 1000)
      : elapsedSeconds;

    // Guard anti-acidente (incidente Enrico 06/jul: um toque de 3s no gravador marcou
    // o paciente como "atendido"). Gravação < 4s = toque sem querer → descarta, não
    // salva nada. Assim nenhum registro fantasma vira "atendido" nem lixo no banco.
    const MIN_RECORDING_SECONDS = 4;
    if (finalElapsed < MIN_RECORDING_SECONDS) {
      lastBlobRef.current = null;
      chunksRef.current = [];
      // Toque acidental: invalida a sessão de backup pra o recovery NÃO recuperar lixo.
      const marcouFalha = markSession('failed');
      cleanupBackupChunks();
      if (sessionIdRef.current) vaultDropSession(sessionIdRef.current); // toque acidental: cofre não guarda lixo
      setError(i18n.language === 'pt'
        ? 'Gravação muito curta — toque e fale por alguns segundos.'
        : 'Recording too short — tap and speak for a few seconds.');
      setStatus('idle');
      marcouFalha.then(() => avisarFimDaGravacao(sessaoDoFim, 'descartada'));
      return;
    }

    const ext = blob.type.includes('mp4') ? 'mp4' : 'webm';
    const recordingUuid = crypto.randomUUID();
    const storagePath = `${patientId}/${recordingUuid}.${ext}`;

    try {
      // ──────────────────────────────────────────────────────
      // Step 1: Upload audio to Supabase Storage (NEVER lose audio)
      // ──────────────────────────────────────────────────────
      setStatus('uploading');

      const { error: uploadError } = await supabase.storage
        .from('consultation-audio')
        .upload(storagePath, blob, {
          contentType: blob.type,
          upsert: false,
        });

      if (uploadError) {
        throw new Error(`Upload failed: ${uploadError.message}`);
      }

      // ──────────────────────────────────────────────────────
      // Step 2: Create DB record IMMEDIATELY (audio is safe now)
      // ──────────────────────────────────────────────────────
      const plaudFileId = `${source}:${recordingUuid}`;
      const fileName = `${fileLabel} — ${new Date().toLocaleDateString('pt-BR')}`;

      const { data: insertedRow, error: dbError } = await supabase
        .from('plaud_recordings')
        .insert({
          plaud_file_id: plaudFileId,
          patient_id: patientId,
          file_name: fileName,
          start_time: startTimeRef.current,
          duration_ms: finalElapsed * 1000,
          match_status: 'matched',
          match_confidence: 1.0,
          source,
          audio_storage_path: storagePath,
        })
        .select('id')
        .single();

      if (dbError) {
        throw new Error(`DB save failed: ${dbError.message}`);
      }

      const recordingId = insertedRow.id;

      // Áudio salvo com sucesso pelo fluxo normal → encerra a sessão de backup
      // (o recovery não toca mais nela) e limpa os chunks de rascunho do Storage.
      // quem espera "Finalizar a outra" só começa com a sessão já fora de 'recording' no banco
      markSession('finalized').then(() => avisarFimDaGravacao(sessaoDoFim, 'salva'));
      cleanupBackupChunks();
      // Gravação inteira salva pelo caminho normal → o cofre local esvazia.
      if (sessionIdRef.current) vaultDropSession(sessionIdRef.current);

      // ──────────────────────────────────────────────────────
      // Step 3: Transcribe (audio is already safe — failure is OK)
      // ──────────────────────────────────────────────────────
      setStatus('transcribing');
      let transcriptText = '';

      try {
        const formData = new FormData();
        formData.append('file', blob, `recording.${ext}`);

        const transcribeRes = await fetchTranscricao(`${webhookUrl}/api/transcribe`, {
          method: 'POST',
          body: formData,
        });

        if (!transcribeRes.ok) {
          const errBody = await transcribeRes.text();
          throw new Error(`Transcription failed: ${errBody}`);
        }

        const transcribeData = await transcribeRes.json();
        transcriptText = transcribeData.transcript || transcribeData.text || '';
        setTranscript(transcriptText);

        // Save raw transcript immediately
        await supabase
          .from('plaud_recordings')
          .update({
            raw_transcript: transcriptText,
            medical_summary: transcriptText,
          })
          .eq('id', recordingId);
      } catch (transcribeErr: any) {
        if (transcribeErr instanceof SemSessao) {
          // Sessão do navegador expirou: áudio e registro já estão salvos e o
          // servidor transcreve toda gravação do portal (gatilho da mig 074).
          // Não é falha — nada de [transcription_failed] nem aviso vermelho.
          console.warn('[useAudioRecorder] sem sessão: a transcrição segue pelo servidor');
          setTranscrevendoNoServidor(true);
          setStatus('done');
          return;
        }
        console.error('[useAudioRecorder] Transcription failed (audio saved):', transcribeErr);
        // Mark as failed but DON'T throw — audio is safe
        await supabase
          .from('plaud_recordings')
          .update({ medical_summary: `[transcription_failed] ${transcribeErr.message}` })
          .eq('id', recordingId);
        // Still set done — recording was saved
        setError(i18n.language === 'pt'
          ? `Áudio salvo! Transcrição falhou: ${transcribeErr.message}. Pode tentar novamente.`
          : `Audio saved! Transcription failed: ${transcribeErr.message}. You can retry.`);
        setStatus('done');
        return;
      }

      // ──────────────────────────────────────────────────────
      // Step 4: Structure with Claude (audio + transcript safe)
      // ──────────────────────────────────────────────────────
      setStatus('structuring');

      try {
        const structureRes = await fetchTranscricao(`${webhookUrl}/api/transcribe/structure`, {
          method: 'POST',
          body: JSON.stringify({ transcript: transcriptText, patient_name: patientName, language: i18n.language }),
        }, { 'Content-Type': 'application/json' });

        if (!structureRes.ok) {
          const errBody = await structureRes.text();
          throw new Error(`Structuring failed: ${errBody}`);
        }

        const structuredData = await structureRes.json();

        // Update with structured data
        await supabase
          .from('plaud_recordings')
          .update({
            structured_data: structuredData,
            processed_at: new Date().toISOString(),
          })
          .eq('id', recordingId);
      } catch (structureErr: any) {
        if (structureErr instanceof SemSessao) {
          // mesma coisa no passo da estruturação: o servidor completa
          console.warn('[useAudioRecorder] sem sessão na estruturação: segue pelo servidor');
          // a transcrição já está gravada: se a rota assíncrona chegar depois dela,
          // ela pula esta gravação. processed_at deixa a aba Consultas oferecer
          // "Tentar novamente" na estruturação (achado do Auditor, 28/09).
          await supabase
            .from('plaud_recordings')
            .update({ processed_at: new Date().toISOString() })
            .eq('id', recordingId)
            .then(undefined, () => undefined);
          setTranscrevendoNoServidor(true);
          setStatus('done');
          return;
        }
        console.error('[useAudioRecorder] Structuring failed (transcript saved):', structureErr);
        // Mark processed_at so we know transcription succeeded but structuring failed
        await supabase
          .from('plaud_recordings')
          .update({ processed_at: new Date().toISOString() })
          .eq('id', recordingId);
        setError(i18n.language === 'pt'
          ? `Áudio e transcrição salvos! Estruturação falhou: ${structureErr.message}. Pode tentar novamente.`
          : `Audio and transcript saved! Structuring failed: ${structureErr.message}. You can retry.`);
        setStatus('done');
        return;
      }

      // ──────────────────────────────────────────────────────
      // Step 5: Everything succeeded — trigger briefing regen + visit summary pre-gen
      // ──────────────────────────────────────────────────────
      webhookFetch(`${webhookUrl}/api/briefing/regenerate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ patient_id: patientId }),
      }).catch((err) => console.warn('[useAudioRecorder] Briefing regen:', err.message));

      // Pre-generate patient-friendly visit summary in background (cache for instant open later)
      webhookFetch(`${webhookUrl}/api/visit-summary/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ recording_id: recordingId }),
      }).catch((err) => console.warn('[useAudioRecorder] Visit summary pre-gen:', err.message));

      setStatus('done');
    } catch (err: any) {
      // Only reaches here if UPLOAD or initial DB INSERT failed
      avisarFimDaGravacao(sessaoDoFim, 'falhou');
      if (err?.name === 'AbortError' || err?.message?.includes('aborted')) {
        console.warn('[useAudioRecorder] Fetch aborted (non-critical):', err.message);
        return;
      }
      console.error('[useAudioRecorder] Pipeline error:', err);
      setError(err?.message || (i18n.language === 'pt' ? 'Erro no processamento' : 'Processing error'));
      setStatus('error');
    }
  }, [patientId, patientName, stopKeepAlive, markSession, cleanupBackupChunks]);

  /**
   * Retry transcription for a recording that has audio saved but failed to transcribe.
   * Called from ConsultationCard's retry button.
   */
  const retryTranscription = useCallback(
    async (recordingId: string, audioPath: string) => {
      const webhookUrl = getWebhookUrl();

      try {
        setError(null);
        setStatus('transcribing');

        // Download audio from Supabase Storage
        const { data: audioData, error: dlError } = await supabase.storage
          .from('consultation-audio')
          .download(audioPath);

        if (dlError || !audioData) {
          throw new Error(`Download failed: ${dlError?.message || 'no data'}`);
        }

        // Transcribe
        const ext = audioPath.endsWith('.mp4') ? 'mp4' : 'webm';
        const formData = new FormData();
        formData.append('file', audioData, `recording.${ext}`);

        const transcribeRes = await fetchTranscricao(`${webhookUrl}/api/transcribe`, {
          method: 'POST',
          body: formData,
        });

        if (!transcribeRes.ok) {
          const errBody = await transcribeRes.text();
          throw new Error(`Transcription failed: ${errBody}`);
        }

        const transcribeData = await transcribeRes.json();
        const transcriptText = transcribeData.transcript || transcribeData.text || '';
        setTranscript(transcriptText);

        // Save raw transcript
        await supabase
          .from('plaud_recordings')
          .update({
            raw_transcript: transcriptText,
            medical_summary: transcriptText,
          })
          .eq('id', recordingId);

        // Structure with Claude
        setStatus('structuring');

        const structureRes = await fetchTranscricao(`${webhookUrl}/api/transcribe/structure`, {
          method: 'POST',
          body: JSON.stringify({ transcript: transcriptText, patient_name: patientName, language: i18n.language }),
        }, { 'Content-Type': 'application/json' });

        if (!structureRes.ok) {
          const errBody = await structureRes.text();
          throw new Error(`Structuring failed: ${errBody}`);
        }

        const structuredData = await structureRes.json();

        // Update DB
        await supabase
          .from('plaud_recordings')
          .update({
            structured_data: structuredData,
            processed_at: new Date().toISOString(),
          })
          .eq('id', recordingId);

        // Trigger briefing regen + visit summary pre-gen
        webhookFetch(`${webhookUrl}/api/briefing/regenerate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ patient_id: patientId }),
        }).catch((err) => console.warn('[useAudioRecorder] Briefing regen:', err.message));

        webhookFetch(`${webhookUrl}/api/visit-summary/generate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ recording_id: recordingId }),
        }).catch((err) => console.warn('[useAudioRecorder] Visit summary pre-gen:', err.message));

        setStatus('done');
      } catch (err: any) {
        console.error('[useAudioRecorder] Retry error:', err);
        // num "tentar de novo" o servidor não refaz sozinho: sem sessão, é pedir login
        setError(err instanceof SemSessao
          ? (i18n.language === 'pt' ? 'Sua sessão expirou. Entre de novo no portal e tente outra vez.' : 'Your session expired. Sign in to the portal again and retry.')
          : (err?.message || (i18n.language === 'pt' ? 'Erro ao tentar novamente' : 'Retry failed')));
        setStatus('error');
      }
    },
    [patientId, patientName],
  );

  /** Whether a saved blob exists for retry (upload failed but audio preserved) */
  const hasSavedBlob = status === 'error' && !!lastBlobRef.current;

  const reset = useCallback(() => {
    setStatus('idle');
    setElapsedSeconds(0);
    setError(null);
    setTranscript(null);
    setBackgroundWarning(false);
    setTranscrevendoNoServidor(false);
    setChunksSaved(0);
    chunksRef.current = [];
    lastBlobRef.current = null;
    sessionIdRef.current = null;
    storagePrefixRef.current = null;
    startTimeRef.current = null;
    startTimestampRef.current = 0;
    stopKeepAlive();
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  }, [stopKeepAlive]);

  /** Retry the full upload+transcribe pipeline using the preserved blob */
  const retryUpload = useCallback(async () => {
    const blob = lastBlobRef.current;
    if (!blob) {
      setError(i18n.language === 'pt' ? 'Nenhum áudio preservado para tentar novamente' : 'No saved audio to retry');
      return;
    }

    const webhookUrl = getWebhookUrl();
    const ext = blob.type.includes('mp4') ? 'mp4' : 'webm';
    const recordingUuid = crypto.randomUUID();
    const storagePath = `${patientId}/${recordingUuid}.${ext}`;

    try {
      setError(null);
      setStatus('uploading');

      const { error: uploadError } = await supabase.storage
        .from('consultation-audio')
        .upload(storagePath, blob, { contentType: blob.type, upsert: false });

      if (uploadError) throw new Error(`Upload failed: ${uploadError.message}`);

      // Create DB record — mesmo payload do fluxo principal
      // (start_time/duration_ms são NOT NULL; recorded_at/created_at não existem na tabela)
      const { data: insertedRow, error: dbError } = await supabase
        .from('plaud_recordings')
        .insert({
          plaud_file_id: `${source}:${recordingUuid}`,
          patient_id: patientId,
          matched_patient_name: patientName,
          file_name: `${fileLabel} — ${new Date().toLocaleDateString('pt-BR')}`,
          start_time: startTimeRef.current || new Date().toISOString(),
          duration_ms: elapsedSeconds * 1000,
          match_status: 'matched',
          match_confidence: 1.0,
          source,
          audio_storage_path: storagePath,
        })
        .select('id')
        .single();

      if (dbError) throw new Error(`DB save failed: ${dbError.message}`);

      const recordingId = insertedRow.id;

      // Try transcription
      setStatus('transcribing');
      const formData = new FormData();
      formData.append('file', blob, `recording.${ext}`);

      let transcribeRes: Response;
      try {
        transcribeRes = await fetchTranscricao(`${webhookUrl}/api/transcribe`, {
          method: 'POST',
          body: formData,
        });
      } catch (e: any) {
        if (!(e instanceof SemSessao)) throw e;
        // registro já salvo: o servidor transcreve sozinho — não é falha
        setTranscrevendoNoServidor(true);
        setStatus('done');
        lastBlobRef.current = null;
        return;
      }

      if (!transcribeRes.ok) {
        const errBody = await transcribeRes.text().catch(() => '');
        // Marca a falha no registro pra aba Consultas mostrar o banner de retry
        await supabase
          .from('plaud_recordings')
          .update({ medical_summary: `[transcription_failed] ${errBody.slice(0, 300)}` })
          .eq('id', recordingId);
        setError(i18n.language === 'pt'
          ? 'Áudio salvo! Transcrição falhou — tente novamente na aba Consultas.'
          : 'Audio saved! Transcription failed — retry available in the Consultations tab.');
        setStatus('done');
        lastBlobRef.current = null;
        return;
      }

      const transcribeData = await transcribeRes.json();
      const transcriptText = transcribeData.transcript || transcribeData.text || '';
      setTranscript(transcriptText);

      // Update DB with transcript (pelo id retornado — o prefixo do plaud_file_id varia com source)
      await supabase
        .from('plaud_recordings')
        .update({ raw_transcript: transcriptText, medical_summary: transcriptText })
        .eq('id', recordingId);

      setStatus('done');
      lastBlobRef.current = null;
    } catch (err: any) {
      console.error('[useAudioRecorder] Retry upload error:', err);
      setError(err?.message || (i18n.language === 'pt' ? 'Erro ao tentar novamente' : 'Retry failed'));
      setStatus('error');
      // blob still preserved in lastBlobRef for another attempt
    }
  }, [patientId, patientName, source, fileLabel, elapsedSeconds]);

  return {
    iniciando,
    status,
    elapsedSeconds,
    error,
    transcript,
    hasSavedBlob,
    backgroundWarning,
    chunksSaved,
    uploadStalled,
    backupEnabled: STREAMING_BACKUP_ENABLED,
    transcrevendoNoServidor,
    outraGravacao,
    limparOutraGravacao: () => setOutraGravacao(null),
    startRecording,
    stopRecording,
    reset,
    retryUpload,
    retryTranscription,
  };
}
